import { View, Text } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import type { DogStats } from "../app/lib/types";

interface TitleProgressProps {
  stats: DogStats;
  /** Average points the dog earns per logged run — used to project runs to next title */
  avgPointsPerRun?: number;
  /** How many runs the avg is based on (≥1 to render the callout) */
  runsLogged?: number;
}

export function TitleProgress({ stats, avgPointsPerRun, runsLogged }: TitleProgressProps) {
  const { currentTitle, nextTitle, pointsToNextTitle, totalPoints } = stats;

  // Determine thresholds for progress bar
  const titleThresholds: Record<string, number> = {
    BCAT: 150,
    DCAT: 500,
    FCAT: 1000,
  };
  // FCAT2+: every 500 after 1000
  const getFcatNThreshold = (n: number) => 1000 + n * 500;

  let rangeStart = 0;
  let rangeEnd = 150;

  if (nextTitle) {
    if (nextTitle === "BCAT") {
      rangeStart = 0;
      rangeEnd = 150;
    } else if (nextTitle === "DCAT") {
      rangeStart = 150;
      rangeEnd = 500;
    } else if (nextTitle === "FCAT") {
      rangeStart = 500;
      rangeEnd = 1000;
    } else if (nextTitle?.startsWith("FCAT")) {
      // FCAT2, FCAT3, etc.
      const n = parseInt(nextTitle.replace("FCAT", ""), 10);
      rangeStart = getFcatNThreshold(n - 1);
      rangeEnd = getFcatNThreshold(n);
    }
  } else if (currentTitle) {
    // Earned last title — show 100% complete
    rangeStart = rangeEnd = totalPoints;
  }

  const progress = rangeEnd > rangeStart
    ? Math.min((totalPoints - rangeStart) / (rangeEnd - rangeStart), 1)
    : 1;

  return (
    <View
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 14,
        padding: 20,
        borderWidth: 1,
        borderColor: "#e5e7eb",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
        marginBottom: 16,
      }}
    >
      {nextTitle ? (
        <>
          <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 8 }}>
            <Text style={{ fontSize: 14, color: "#6b7280" }}>
              {totalPoints.toFixed(1)} pts
            </Text>
            <Text style={{ fontSize: 14, color: "#1B2A4A", fontWeight: "600" }}>
              {nextTitle} — {pointsToNextTitle.toFixed(1)} pts to go
            </Text>
          </View>
          <View
            style={{
              height: 10,
              backgroundColor: "#e5e7eb",
              borderRadius: 5,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                height: 10,
                width: `${Math.round(progress * 100)}%`,
                backgroundColor: "#F5A623",
                borderRadius: 5,
              }}
            />
          </View>
          <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 6 }}>
            <Text style={{ fontSize: 15, color: "#9ca3af" }}>{rangeStart} pts</Text>
            <Text style={{ fontSize: 15, color: "#9ca3af" }}>{rangeEnd} pts</Text>
          </View>

          {/* Pace projection — only when we have logged runs to derive an average from */}
          {avgPointsPerRun && avgPointsPerRun > 0 && runsLogged && runsLogged > 0 ? (() => {
            const runsNeeded = Math.max(1, Math.ceil(pointsToNextTitle / avgPointsPerRun));
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 14,
                  backgroundColor: "#FFF7E6",
                  borderLeftWidth: 3,
                  borderLeftColor: "#F5A623",
                  borderRadius: 6,
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                }}
              >
                <Ionicons name="flash" size={18} color="#F5A623" />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 13, color: "#6b7280" }}>
                    Averaging <Text style={{ fontWeight: "700", color: "#1B2A4A" }}>{avgPointsPerRun.toFixed(1)} pts/run</Text>
                  </Text>
                  <Text style={{ fontSize: 14, color: "#1B2A4A", marginTop: 2 }}>
                    About <Text style={{ fontWeight: "800", color: "#F5A623" }}>{runsNeeded} more {runsNeeded === 1 ? "run" : "runs"}</Text> to <Text style={{ fontWeight: "700" }}>{nextTitle}</Text> at this pace
                  </Text>
                </View>
              </View>
            );
          })() : null}
        </>
      ) : (
        <View style={{ alignItems: "center", paddingVertical: 8 }}>
          <Text style={{ fontSize: 14, color: "#6b7280" }}>
            {totalPoints > 0 ? "Continuing to earn titles..." : "Log runs to start earning titles!"}
          </Text>
        </View>
      )}
    </View>
  );
}
